import { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAdminManagement, AdminUser } from '../hooks/useAdminManagement';
import { useAuth } from '@/contexts/AuthContext';
import {
  UserPlus,
  Trash2,
  Shield,
  ShieldCheck,
  RefreshCw,
  AlertCircle,
  X,
  Search,
  Crown,
} from 'lucide-react';

export default function AdminTeamPage() {
  const { admins, isLoading, error, fetchAdmins, addAdmin, removeAdmin, updateRole } =
    useAdminManagement();
  const { user } = useAuth();

  const [showAddModal, setShowAddModal] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState<'admin' | 'superadmin'>('admin');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [confirmRemove, setConfirmRemove] = useState<AdminUser | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const filteredAdmins = admins.filter((a) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      a.email.toLowerCase().includes(q) ||
      a.first_name.toLowerCase().includes(q) ||
      a.last_name.toLowerCase().includes(q)
    );
  });

  const handleAdd = async () => {
    if (!addEmail.trim()) return;
    setAddLoading(true);
    setAddError(null);
    const result = await addAdmin(addEmail.trim(), addRole);
    setAddLoading(false);
    if (result.success) {
      setShowAddModal(false);
      setAddEmail('');
      setAddRole('admin');
    } else {
      setAddError(result.error || 'Erreur inconnue');
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    setRemoveLoading(true);
    await removeAdmin(confirmRemove.user_id);
    setRemoveLoading(false);
    setConfirmRemove(null);
  };

  const handleRoleToggle = async (admin: AdminUser) => {
    const newRole = admin.role === 'admin' ? 'superadmin' : 'admin';
    await updateRole(admin.user_id, newRole);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <AdminLayout
      title="Gestion des admins"
      subtitle="Ajouter, supprimer et gérer les rôles des administrateurs"
      action={
        <div className="flex gap-2">
          <button
            onClick={() => fetchAdmins()}
            className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-medium transition-colors shadow-lg shadow-violet-600/25"
          >
            <UserPlus size={18} />
            Ajouter un admin
          </button>
        </div>
      }
    >
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-violet-100 dark:bg-violet-900/30">
              <Shield size={20} className="text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{admins.length}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total admins</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/30">
              <Crown size={20} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {admins.filter((a) => a.role === 'superadmin').length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Super admins</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-green-100 dark:bg-green-900/30">
              <ShieldCheck size={20} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {admins.filter((a) => a.role === 'admin').length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Admins</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={24} className="animate-spin text-violet-500" />
          </div>
        ) : filteredAdmins.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Shield size={48} className="mb-4 opacity-30" />
            <p className="text-lg font-medium">Aucun admin trouvé</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700/50">
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ajouté le
                  </th>
                  <th className="text-right px-6 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filteredAdmins.map((admin) => {
                  const isCurrentUser = admin.user_id === user?.id;
                  const displayName =
                    admin.first_name || admin.last_name
                      ? `${admin.first_name} ${admin.last_name}`.trim()
                      : admin.email;

                  return (
                    <tr
                      key={admin.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                            {(admin.first_name?.[0] || admin.email[0]).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                              {displayName}
                              {isCurrentUser && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400">
                                  Vous
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {admin.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                            admin.role === 'superadmin'
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                              : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          }`}
                        >
                          {admin.role === 'superadmin' ? <Crown size={12} /> : <Shield size={12} />}
                          {admin.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                        {formatDate(admin.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        {!isCurrentUser && (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleRoleToggle(admin)}
                              title={
                                admin.role === 'admin'
                                  ? 'Promouvoir en Super Admin'
                                  : 'Rétrograder en Admin'
                              }
                              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                            >
                              {admin.role === 'admin' ? <Crown size={16} /> : <Shield size={16} />}
                            </button>
                            <button
                              onClick={() => setConfirmRemove(admin)}
                              title="Supprimer l'admin"
                              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl border border-gray-100 dark:border-gray-700/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Ajouter un admin</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddError(null);
                  setAddEmail('');
                }}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {addError && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-red-700 dark:text-red-300 text-sm">{addError}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Email de l'utilisateur
                </label>
                <input
                  type="email"
                  placeholder="user@example.com"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Rôle
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setAddRole('admin')}
                    className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      addRole === 'admin'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <Shield
                      size={18}
                      className={
                        addRole === 'admin' ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
                      }
                    />
                    <span
                      className={`text-sm font-medium ${
                        addRole === 'admin'
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Admin
                    </span>
                  </button>
                  <button
                    onClick={() => setAddRole('superadmin')}
                    className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      addRole === 'superadmin'
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <Crown
                      size={18}
                      className={
                        addRole === 'superadmin'
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-gray-400'
                      }
                    />
                    <span
                      className={`text-sm font-medium ${
                        addRole === 'superadmin'
                          ? 'text-amber-700 dark:text-amber-300'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Super Admin
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddError(null);
                  setAddEmail('');
                }}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleAdd}
                disabled={addLoading || !addEmail.trim()}
                className="flex-1 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-colors shadow-lg shadow-violet-600/25"
              >
                {addLoading ? <RefreshCw size={18} className="animate-spin mx-auto" /> : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Remove Modal */}
      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl border border-gray-100 dark:border-gray-700/50">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <Trash2 size={24} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                Supprimer l'admin ?
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {confirmRemove.first_name
                  ? `${confirmRemove.first_name} ${confirmRemove.last_name}`.trim()
                  : confirmRemove.email}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">
                Cette action est irréversible
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmRemove(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleRemove}
                disabled={removeLoading}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium transition-colors shadow-lg shadow-red-600/25"
              >
                {removeLoading ? (
                  <RefreshCw size={18} className="animate-spin mx-auto" />
                ) : (
                  'Supprimer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
